import { Link, Button, Badge, Card, CardContent, useAuth } from "@/pages/shared";
import { Helmet } from "react-helmet-async";
import { CLAIMS } from "@/config/claims";

const Section = ({ title, children, testId }) => (
  <Card className="glass" data-testid={testId}>
    <CardContent className="p-6">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-2">{children}</div>
    </CardContent>
  </Card>
);

const SUBPROCESSORS = [
  { name: "Emergent (Universal LLM gateway)", purpose: "Routes AI generation requests to model providers", data: "Prompt text you submit for generation" },
  { name: "OpenAI (GPT-5.2, GPT Image 1, Sora 2)", purpose: "AI text, image and video generation", data: "Prompt text / image briefs" },
  { name: "Google (Gemini)", purpose: "AI citation-tracking queries with search grounding", data: "Brand/keyword queries you configure" },
  { name: "Stripe", purpose: "Payments, subscriptions, tax calculation", data: "Billing details (card data never touches our servers)" },
  { name: "Resend", purpose: "Transactional email delivery", data: "Your email address, email content" },
  { name: "MongoDB (managed hosting)", purpose: "Primary application database", data: "Account and content data" },
  { name: "Cloudflare", purpose: "DNS, CDN and edge security", data: "Request metadata (IP, headers)" },
];

const OAUTH_SCOPES = [
  { network: "X (Twitter)", scopes: "OAuth 1.0a — read & write (post tweets)", why: "So scheduled posts can publish to your X account. We never read your DMs." },
  { network: "LinkedIn", scopes: "openid, profile, w_member_social", why: "To identify your profile and publish posts you schedule. No reading of your feed or connections." },
  { network: "Bluesky", scopes: "App password (scoped credential you create)", why: "To publish scheduled cross-posts. Revocable anytime in Bluesky settings." },
  { network: "Telegram", scopes: "Your own bot token", why: "To post to the channel/group you configure. The bot only has the rights you give it." },
  { network: "Google (sign-in)", scopes: "openid, email, profile", why: "Account creation and login only." },
];

export default function TrustPage() {
  const { user } = useAuth();
  const c = CLAIMS.company;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>Trust & Transparency — Creatara</title>
        <meta name="description" content="Who runs Creatara, which subprocessors handle your data, what OAuth scopes we request and why, data retention and deletion, and your responsibilities." />
        <link rel="canonical" href="https://creatara.ai/trust" />
      </Helmet>

      <nav className="border-b border-white/5 sticky top-0 z-40 bg-background/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold">Creatara</Link>
          <Link to={user ? "/dashboard" : "/register"}><Button className="bg-violet-600 hover:bg-violet-700 rounded-full">{user ? "Dashboard" : "Get started"}</Button></Link>
        </div>
      </nav>

      <section className="pt-16 pb-10 px-6">
        <div className="max-w-5xl mx-auto">
          <Badge className="mb-4 bg-violet-600/10 text-violet-500 border-violet-600/20">Trust & Transparency</Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">How we handle your data</h1>
          <p className="text-muted-foreground max-w-2xl">Everything below is the operational reality, not aspiration. If anything here is unclear, email {c.supportEmail}.</p>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-5xl mx-auto space-y-6">
          <Section title="Who we are" testId="trust-legal-entity">
            <p><strong className="text-foreground">{c.legalName}</strong> — {c.registration}, {c.country}.</p>
            <p>Contact address available on request via {c.supportEmail} and included on all invoices issued through Stripe.</p>
          </Section>

          <Section title="Support" testId="trust-support">
            <p>{c.supportResponse} Reach us at <a href={`mailto:${c.supportEmail}`} className="text-violet-400 underline">{c.supportEmail}</a> or via the <Link to="/contact" className="text-violet-400 underline">contact form</Link>.</p>
          </Section>

          <Section title="Subprocessors (including AI model providers)" testId="trust-subprocessors">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-separate border-spacing-y-1">
                <thead><tr className="text-left uppercase tracking-wider text-muted-foreground/70"><th className="pr-4 py-1">Provider</th><th className="pr-4 py-1">Purpose</th><th className="py-1">Data shared</th></tr></thead>
                <tbody>
                  {SUBPROCESSORS.map((s) => (
                    <tr key={s.name}><td className="pr-4 py-1.5 text-foreground font-medium">{s.name}</td><td className="pr-4 py-1.5">{s.purpose}</td><td className="py-1.5">{s.data}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Data retention & deletion" testId="trust-retention">
            <p>Your account data, generated content, tracked-link analytics and captured leads are retained while your account exists so the product keeps working for you.</p>
            <p><strong className="text-foreground">Self-serve deletion:</strong> Settings → Danger Zone → Delete account permanently removes your account and all associated data (content, templates, links, click logs, lead forms and leads). This is immediate and irreversible.</p>
            <p>You can also request deletion or a data export by emailing {c.supportEmail}. Details of your rights are in our <Link to="/privacy" className="text-violet-400 underline">Privacy Policy</Link>.</p>
          </Section>

          <Section title="OAuth scopes we request, and why" testId="trust-oauth">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-separate border-spacing-y-1">
                <thead><tr className="text-left uppercase tracking-wider text-muted-foreground/70"><th className="pr-4 py-1">Network</th><th className="pr-4 py-1">Scopes</th><th className="py-1">Why</th></tr></thead>
                <tbody>
                  {OAUTH_SCOPES.map((s) => (
                    <tr key={s.network}><td className="pr-4 py-1.5 text-foreground font-medium">{s.network}</td><td className="pr-4 py-1.5">{s.scopes}</td><td className="py-1.5">{s.why}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2">Full per-network capabilities and limitations: <Link to="/integrations" className="text-violet-400 underline">Integration Matrix</Link>.</p>
          </Section>

          <Section title="Lead-capture consent" testId="trust-leads">
            <p>When someone submits one of your lead forms, we record the submitted fields plus a timestamp. That record is the evidence of the submission and is exportable by you at any time (Leads → Export CSV). You are the data controller for leads you collect — make sure your form and follow-up comply with the consent rules that apply to your audience.</p>
          </Section>

          <Section title="Your disclosure responsibilities" testId="trust-disclosures">
            <p>Creatara helps you attach tracked affiliate links to content, and reminds you to disclose them — but <strong className="text-foreground">you are responsible for your own affiliate and sponsorship disclosures</strong> under the advertising rules that apply to you (e.g. FTC in the US, ASA/CAP in the UK). You can save a reusable disclosure line in Settings and insert it into any post with one click.</p>
          </Section>

          <Section title="Privacy & legal" testId="trust-legal-links">
            <p>Our data-protection commitments (UK GDPR / GDPR) are set out in detail in the <Link to="/privacy" className="text-violet-400 underline">Privacy Policy</Link>. Service terms are in the <Link to="/terms" className="text-violet-400 underline">Terms of Service</Link>.</p>
          </Section>
        </div>
      </section>
    </div>
  );
}
